import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import { Mic, Trophy, Coins, FileText, ChevronRight, Settings, Music, X, Camera, User, Trash2 } from 'lucide-react';
import { DistributionRights } from '../services/DistributionService';
import { UserCoins } from '../services/VirtualCurrencyService';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AvatarCropper } from '../components/AvatarCropper';

export const Profile: React.FC = () => {
  const { user } = useUser();
  const { profile, loading: profileLoading, refreshProfile } = useData();
  const { distributionService, virtualCurrencyService } = useData();
  const navigate = useNavigate();

  const [distributionRights, setDistributionRights] = useState<DistributionRights | null>(null);
  const [userCoins, setUserCoins] = useState<UserCoins | null>(null);
  const [showDistributionAgreement, setShowDistributionAgreement] = useState(false);
  const [rightType, setRightType] = useState<string>('audio');
  const [acceptAgreement, setAcceptAgreement] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', username: '' });
  const [pendingAvatarBlob, setPendingAvatarBlob] = useState<Blob | null>(null);
  const [pendingAvatarPreview, setPendingAvatarPreview] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [loading, setLoading] = useState({
    rights: false,
    coins: false,
  });
  const [recentPerformances, setRecentPerformances] = useState<any[]>([]);
  const [loadingPerformances, setLoadingPerformances] = useState(false);
  const [purchasedSongs, setPurchasedSongs] = useState<any[]>([]);
  const [loadingPurchased, setLoadingPurchased] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState<'performances' | 'purchased'>('performances');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadProfileData();
      loadRecentPerformances();
      loadPurchasedSongs();
    }
  }, [user]);

  const loadProfileData = async () => {
    if (!user?.id) return;

    try {
      setLoading(prev => ({ ...prev, rights: true }));
      const rights = await distributionService.getUserDistributionRights(user.id);
      setDistributionRights(rights);
      setLoading(prev => ({ ...prev, rights: false }));

      setLoading(prev => ({ ...prev, coins: true }));
      const coins = await virtualCurrencyService.getUserCoins(user.id);
      setUserCoins(coins);
      setLoading(prev => ({ ...prev, coins: false }));
    } catch (err) {
      console.error('Error loading profile data:', err);
      setError('Failed to load profile data. Please try again later.');
      setLoading({
        rights: false,
        coins: false,
      });
    }
  };

  const loadRecentPerformances = async () => {
    if (!user?.id) return;
    setLoadingPerformances(true);
    try {
      const { data: recordings } = await supabase
        .from('recordings')
        .select(`
          id,
          title,
          created_at,
          song_id,
          song:songs (title, artist, cover_url)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(6);

      if (recordings && recordings.length > 0) {
        const { data: performancesWithScores } = await supabase
          .from('performances')
          .select(`
            recording_id,
            performance_analysis (overall_score)
          `)
          .in('recording_id', recordings.map(r => r.id));

        const scoresMap = new Map<string, number>();
        if (performancesWithScores) {
          performancesWithScores.forEach((p: any) => {
            if (p.performance_analysis && p.performance_analysis.length > 0) {
              const score = p.performance_analysis[0].overall_score;
              if (score && score > 0) {
                scoresMap.set(p.recording_id, score);
              }
            }
          });
        }

        setRecentPerformances(recordings.map(r => ({
          ...r,
          score: scoresMap.get(r.id) || null,
        })));
      } else {
        setRecentPerformances([]);
      }
    } catch (err) {
      console.error('Error loading recent performances:', err);
    } finally {
      setLoadingPerformances(false);
    }
  };

  const loadPurchasedSongs = async () => {
    if (!user?.id) return;
    setLoadingPurchased(true);
    try {
      const { data } = await supabase
        .from('song_library')
        .select(`
          id,
          unlocked_at,
          cost,
          song:songs (id, title, artist, cover_url, duration)
        `)
        .eq('user_id', user.id)
        .order('unlocked_at', { ascending: false });

      setPurchasedSongs(data || []);
    } catch (err) {
      console.error('Error loading purchased songs:', err);
    } finally {
      setLoadingPurchased(false);
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleAcceptAgreement = async () => {
    if (!user?.id) return;

    if (!acceptAgreement) {
      setError('You must accept the agreement to continue.');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, rights: true }));
      setError(null);
      const updatedRights = await distributionService.acceptDistributionAgreement(
        user.id,
        rightType,
        'v1.0'
      );
      setDistributionRights(updatedRights);
      setShowDistributionAgreement(false);
      setSuccessMessage('Distribution agreement accepted successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error accepting agreement:', err);
      setError('Failed to accept agreement. Please try again later.');
    } finally {
      setLoading(prev => ({ ...prev, rights: false }));
    }
  };

  const openEditProfile = () => {
    setEditForm({
      full_name: profile?.full_name || '',
      username: profile?.username || '',
    });
    setPendingAvatarBlob(null);
    setPendingAvatarPreview(null);
    setShowEditProfile(true);
  };

  const handleAvatarCropped = (blob: Blob) => {
    setPendingAvatarBlob(blob);
    const url = URL.createObjectURL(blob);
    setPendingAvatarPreview(url);
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    setSavingProfile(true);
    setError(null);
    try {
      let avatarUrl = profile?.avatar_url || null;

      if (pendingAvatarBlob) {
        const fileName = `${user.id}/avatar_${Date.now()}.png`;
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, pendingAvatarBlob, {
            cacheControl: '3600',
            upsert: true,
            contentType: 'image/png',
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);

        avatarUrl = urlData.publicUrl;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: editForm.full_name,
          username: editForm.username,
          avatar_url: avatarUrl,
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      await refreshProfile();
      setShowEditProfile(false);
      setPendingAvatarBlob(null);
      if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
      setPendingAvatarPreview(null);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      if (err?.message?.includes('unique') || err?.code === '23505') {
        setError('That username is already taken. Please choose another.');
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Your Profile</h1>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-500">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 text-green-500">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Profile Overview */}
        <div className="card flex flex-col">
          <div className="flex flex-col items-center text-center flex-1">
            <div className="relative mb-4">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border-4 border-kod-accent"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-blue-600 border-4 border-kod-accent flex items-center justify-center">
                  <User size={36} className="text-white" />
                </div>
              )}
            </div>
            <h2 className="text-xl font-bold">{profile?.full_name || "Loading..."}</h2>
            <p className="text-white/70 text-sm mb-2">@{profile?.username || "username"}</p>
            <p className="text-white/50 text-xs mb-4">{user?.email || ''}</p>
          </div>
          <button
            className="btn-outline w-full flex items-center justify-center"
            onClick={openEditProfile}
          >
            <Settings size={16} className="mr-2" />
            Edit Profile
          </button>
        </div>

        {/* Edit Profile Modal */}
        {showEditProfile && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
            <div className="bg-kod-purple-800 rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Edit Profile</h2>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className="text-white/50 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-6">
                <label className="text-sm font-medium text-white/70 mb-3 block">Profile Photo</label>
                {pendingAvatarPreview ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={pendingAvatarPreview}
                      alt="New avatar"
                      className="w-24 h-24 rounded-full object-cover border-4 border-green-500/50 mb-3"
                    />
                    <p className="text-green-400 text-xs mb-2">New photo ready</p>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingAvatarBlob(null);
                        if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
                        setPendingAvatarPreview(null);
                      }}
                      className="text-xs text-white/50 hover:text-white underline"
                    >
                      Remove and choose another
                    </button>
                  </div>
                ) : (
                  <AvatarCropper
                    currentAvatarUrl={profile?.avatar_url || null}
                    onCropComplete={handleAvatarCropped}
                    onCancel={() => {}}
                  />
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-white/70 mb-1 block">Full Name</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={editForm.full_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-white/70 mb-1 block">Username</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={editForm.username}
                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Your username"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-outline"
                  onClick={() => setShowEditProfile(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                >
                  {savingProfile ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TSL Coins */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Coins size={18} className="mr-2 text-yellow-400" />
            TSL Coins
          </h3>

          {loading.coins ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
            </div>
          ) : (
            <>
              <div className="bg-kod-purple-700/60 rounded-lg p-4 flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-white/70 mb-1">Current Balance</div>
                  <div className="text-2xl font-bold flex items-center">
                    <Coins size={18} className="text-yellow-400 mr-2" />
                    {userCoins?.balance || 0}
                  </div>
                </div>
                <Link to="/purchase-coins" className="btn-primary">
                  Add Coins
                </Link>
              </div>

              <div className="space-y-4">
                <Link
                  to="/tournaments"
                  className="flex items-center justify-between p-2 hover:bg-kod-purple-700/60 rounded-lg transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3">
                      <Trophy size={16} className="text-yellow-400" />
                    </div>
                    <div>
                      <div className="font-medium">Tournament Entry</div>
                      <div className="text-xs text-white/60">Use coins to enter singing tournaments</div>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-white/40" />
                </Link>

                <Link
                  to="/purchase-coins"
                  className="flex items-center justify-between p-2 hover:bg-kod-purple-700/60 rounded-lg transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-kod-purple-700 flex items-center justify-center mr-3">
                      <Gift size={16} className="text-kod-accent" />
                    </div>
                    <div>
                      <div className="font-medium">Transaction History</div>
                      <div className="text-xs text-white/60">View your coin transactions</div>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-white/40" />
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Distribution Rights */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FileText size={18} className="mr-2 text-kod-accent" />
            Distribution Rights
          </h3>

          {loading.rights ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
            </div>
          ) : (
            <>
              {distributionRights?.agreement_accepted ? (
                <div>
                  <div className="bg-green-500/10 text-green-500 rounded-lg p-3 mb-4 flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center mr-3">
                      <FileText size={16} className="text-green-500" />
                    </div>
                    <div>
                      <div className="font-medium">Agreement Accepted</div>
                      <div className="text-sm text-green-500/70">
                        {new Date(distributionRights.agreement_date || "").toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/70">Rights Type:</span>
                      <span className="font-medium">{distributionRights.rights_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Version:</span>
                      <span className="font-medium">{distributionRights.agreement_version}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-white/70 mb-4">
                    You haven't accepted the distribution agreement yet. Accept the agreement to distribute your performances.
                  </p>
                  <button
                    className="btn-primary w-full"
                    onClick={() => setShowDistributionAgreement(true)}
                  >
                    Accept Agreement
                  </button>
                </div>
              )}
            </>
          )}

          {showDistributionAgreement && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">Distribution Agreement</h2>
                <div className="bg-kod-purple-700 rounded-lg p-4 mb-4 h-64 overflow-y-auto text-sm text-white/80">
                  <h3 className="font-bold mb-2">Distribution Terms & Conditions</h3>
                  <p className="mb-3">
                    By accepting this agreement, you authorize Karaoke On Demand to distribute your recorded performances to third-party platforms including but not limited to YouTube, YouTube Music, and other streaming services.
                  </p>
                  <p className="mb-3">
                    You confirm that:
                  </p>
                  <ol className="list-decimal pl-5 mb-3 space-y-2">
                    <li>You have the right to distribute performances of the songs you record.</li>
                    <li>You understand that revenue generated from these distributions will be shared according to the platform's revenue split policy (currently 70% artist, 30% platform).</li>
                    <li>You grant Karaoke On Demand the right to distribute your content and collect revenue on your behalf.</li>
                    <li>You understand distribution is subject to approval and may be removed at the platform's discretion.</li>
                  </ol>
                  <p>
                    This agreement can be terminated at any time, but any content already distributed will remain available until explicitly removed.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="text-sm font-medium mb-1 block">Distribution Rights Type</label>
                  <select
                    className="input"
                    value={rightType}
                    onChange={(e) => setRightType(e.target.value)}
                  >
                    <option value="audio">Audio Only</option>
                    <option value="video">Video Only</option>
                    <option value="both">Both Audio & Video</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2 h-4 w-4 accent-kod-accent"
                      checked={acceptAgreement}
                      onChange={(e) => setAcceptAgreement(e.target.checked)}
                    />
                    <span className="text-sm">I accept the distribution agreement terms</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    className="btn-outline"
                    onClick={() => setShowDistributionAgreement(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleAcceptAgreement}
                    disabled={loading.rights || !acceptAgreement}
                  >
                    {loading.rights ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      "Accept Agreement"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Performances / Purchased Songs Tabs */}
      <div className="card mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1 bg-kod-purple-800/60 rounded-lg p-1">
            <button
              onClick={() => setActiveProfileTab('performances')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeProfileTab === 'performances'
                  ? 'bg-kod-accent text-white shadow'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Recent Performances
            </button>
            <button
              onClick={() => setActiveProfileTab('purchased')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeProfileTab === 'purchased'
                  ? 'bg-kod-accent text-white shadow'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Purchased Songs
            </button>
          </div>
          {activeProfileTab === 'performances' && (
            <Link to="/recordings" className="text-sm text-kod-accent hover:underline flex items-center">
              View All <ChevronRight size={16} />
            </Link>
          )}
          {activeProfileTab === 'purchased' && (
            <Link to="/song-library" className="text-sm text-kod-accent hover:underline flex items-center">
              Browse Songs <ChevronRight size={16} />
            </Link>
          )}
        </div>

        {activeProfileTab === 'performances' && (
          <>
            {loadingPerformances ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
              </div>
            ) : recentPerformances.length === 0 ? (
              <div className="text-center py-8 text-white/50">
                <Mic size={32} className="mx-auto mb-3 opacity-50" />
                <p>No performances yet.</p>
                <Link to="/songs" className="text-kod-accent hover:underline text-sm mt-2 inline-block">
                  Browse songs to get started
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recentPerformances.map((rec) => {
                  const song = rec.song as any;
                  const score = rec.score ? Math.round(Number(rec.score)) : null;

                  return (
                    <Link
                      key={rec.id}
                      to={`/recordings`}
                      className="bg-kod-purple-700/60 rounded-lg overflow-hidden hover:bg-kod-purple-700/80 transition-colors group"
                    >
                      <div className="relative h-40">
                        {song?.cover_url ? (
                          <img
                            src={song.cover_url}
                            alt={song.title || rec.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-kod-purple-700 flex items-center justify-center">
                            <Music size={40} className="text-white/20" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
                          <div>
                            <h4 className="font-bold">{song?.title || rec.title || 'Untitled'}</h4>
                            <p className="text-sm text-white/70">{song?.artist || ''}</p>
                          </div>
                        </div>
                        {score !== null && (
                          <div className="absolute top-2 right-2 bg-kod-purple-800/80 px-2 py-1 rounded text-xs font-bold">
                            Score: {score}
                          </div>
                        )}
                      </div>
                      <div className="p-3 flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <Mic size={14} className="text-kod-accent" />
                          <span className="text-sm text-white/70">
                            {song?.title || 'Recording'}
                          </span>
                        </div>
                        <div className="text-xs text-white/50">{formatTimeAgo(rec.created_at)}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeProfileTab === 'purchased' && (
          <>
            {loadingPurchased ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
              </div>
            ) : purchasedSongs.length === 0 ? (
              <div className="text-center py-8 text-white/50">
                <Music size={32} className="mx-auto mb-3 opacity-50" />
                <p>No purchased songs yet.</p>
                <Link to="/song-library" className="text-kod-accent hover:underline text-sm mt-2 inline-block">
                  Browse the song library
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {purchasedSongs.map((entry) => {
                  const song = entry.song as any;
                  return (
                    <button
                      key={entry.id}
                      onClick={() => navigate(`/kod-studio/${song?.id}`)}
                      className="bg-kod-purple-700/60 rounded-lg overflow-hidden hover:bg-kod-purple-700/80 transition-colors group text-left w-full"
                    >
                      <div className="relative h-40">
                        {song?.cover_url ? (
                          <img
                            src={song.cover_url}
                            alt={song.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-kod-purple-700 flex items-center justify-center">
                            <Music size={40} className="text-white/20" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
                          <div>
                            <h4 className="font-bold">{song?.title || 'Unknown Song'}</h4>
                            <p className="text-sm text-white/70">{song?.artist || ''}</p>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-kod-accent rounded-full p-1.5">
                          <Mic size={14} className="text-white" />
                        </div>
                      </div>
                      <div className="p-3 flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <Coins size={14} className="text-yellow-400" />
                          <span className="text-sm text-white/70">{entry.cost} coins</span>
                        </div>
                        <div className="text-xs text-white/50">{formatTimeAgo(entry.unlocked_at)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Account */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <Link
          to="/delete-account"
          className="flex items-center gap-3 text-red-400/70 hover:text-red-400 transition group"
        >
          <Trash2 size={16} />
          <span className="text-sm">Delete Account</span>
          <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
        </Link>
      </div>
    </div>
  );
};

function Gift(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
    </svg>
  );
}
